import { useMemo } from 'preact/hooks';
import { flagAssetFor } from './flags';
import type { PublicClassStatusFS, PublicResultRowFS } from './types';

/**
 * Rows fade in one after another when results load. The stagger stops growing
 * after this many rows, so `limit=all` on a big class does not leave the tail
 * blank for seconds.
 */
const STAGGER_ROWS = 10;

/**
 * Rounds are rendered exactly as stored. The scoring path has already formatted
 * them — `"0 | 71.8"` for a jumping round, a bare score for hunters and
 * equitation, a status such as `EL` — so the widget passes them straight through
 * rather than restyling them.
 *
 * The one rule, ported from ED-WEB's `splitRound`: `''` and `'-'` both mean the
 * round was not recorded, which is not the same as a round with no faults, so
 * neither may render as a `0`. That distinction is why rounds travel as these
 * strings and not as the numeric faults/time columns, which store 0 for both and
 * put a phantom jump-off against every entry in a one-round class.
 */
function hasRound(result: string): boolean {
  const raw = (result ?? '').trim();
  return raw !== '' && raw !== '-';
}

function formatRound(result: string): string {
  const raw = (result ?? '').trim();
  return hasRound(raw) ? raw : '—';
}

/**
 * Two presentation rules key off the class lifecycle, in opposite directions:
 *
 * - **In-ring highlight, only while in progress.** `inRing` is a live-scoring flag
 *   that is not cleared when a class ends, so a finished class keeps whichever
 *   entry happened to be in the ring last. Highlighting it there points spectators
 *   at a round that is over.
 * - **Medals, only once finished.** A placing mid-class is provisional — the
 *   leader can be displaced by any round still to jump — so a medal on a live or
 *   scheduled class asserts a podium that has not been decided.
 */
export function ResultsTable({
  rows,
  status,
}: {
  rows: PublicResultRowFS[];
  status: PublicClassStatusFS;
}) {
  const isLive = status === 'in_progress';
  const isFinished = status === 'finished';
  // A jump-off column on a class that never had one is a column of em dashes, so
  // it appears only once some entry has actually jumped off. Prize money is the
  // same: most classes carry none, and an empty money column reads as an error.
  const showJumpOff = useMemo(() => rows.some((row) => hasRound(row.result2)), [rows]);
  const showPrize = useMemo(() => rows.some((row) => row.prize.trim() !== ''), [rows]);

  return (
    <div class="table-scroll">
      <table class="results">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Rider</th>
            <th scope="col">Horse</th>
            <th scope="col" class="num">
              Result
            </th>
            {showJumpOff && (
              <th scope="col" class="num">
                Jump-Off
              </th>
            )}
            {showPrize && (
              <th scope="col" class="num">
                Prize
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const placed = row.rank !== '' && row.rank !== '-';
            // The podium gets a medal once the class is decided; everything else
            // keeps the plain numeral.
            const placing = placed ? Number(row.rank) : Number.NaN;
            const medal =
              isFinished && Number.isInteger(placing) && placing >= 1 && placing <= 3
                ? placing
                : null;
            const rankClass = medal
              ? `rank__value rank__value--medal rank__value--medal-${medal}`
              : 'rank__value';
            return (
              <tr
                key={row.id}
                class={isLive && row.inRing ? 'row row--in-ring' : 'row'}
                // Keyed rows animate only when they mount: a live score update
                // re-renders the rows already shown without replaying it.
                style={{ '--row-index': Math.min(index, STAGGER_ROWS) }}
              >
                <td class={placed ? 'rank rank--placed' : 'rank'}>
                  {isLive && row.inRing && <span class="in-ring-dot" aria-label="In ring" />}
                  <span class={rankClass}>{placed ? row.rank : '—'}</span>
                </td>
                <td>
                  <span class="rider">{row.riderName}</span>
                  {row.riderAcronym && (
                    <span class="nationality">
                      {/*
                        Decorative: the code is printed right beside it, so
                        announcing the flag as well would just repeat itself.
                        onError covers a code whose asset is missing — the flag
                        disappears rather than leaving a broken-image glyph.
                      */}
                      {flagAssetFor(row.riderAcronym) && (
                        <img
                          class="flag"
                          src={flagAssetFor(row.riderAcronym)}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          onError={(event) => {
                            (event.currentTarget as HTMLImageElement).hidden = true;
                          }}
                        />
                      )}
                      <span class="acronym">{row.riderAcronym}</span>
                    </span>
                  )}
                </td>
                <td>
                  <div class="horse-cell">
                    {row.entryNumber && <span class="entry-chip">{row.entryNumber}</span>}
                    <span class="horse-name">{row.horseName}</span>
                  </div>
                </td>
                <td class="num">
                  <span class="result-primary">{formatRound(row.result1)}</span>
                </td>
                {showJumpOff && (
                  <td class="num">
                    <span class="result-primary">{formatRound(row.result2)}</span>
                  </td>
                )}
                {showPrize && (
                  <td class="num">
                    {/*
                      Already currency-formatted upstream by Intl.NumberFormat
                      ($15,000) and empty when an entry won nothing, so it prints
                      as stored — same contract as the round strings.
                    */}
                    <span class="prize">{row.prize.trim() || '—'}</span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
